import { ResponsiveHeatMap } from "@nivo/heatmap";
import ChartCard from "../../../../shared/charts/ChartCard";
import { NIVO_DARK_THEME } from "./HeadcountChart";

/**
 * codex.md v4 — Utilisation Heatmap. Consultants × months, coloured
 * by util % using a three-band custom scale:
 *
 *   < 10%          → orange  (significantly under-used)
 *   10% – 100%     → light → dark green gradient (healthy band)
 *   > 100%         → red      (over-utilisation)
 *
 * Red is reserved exclusively for over-burn; no warm colours inside
 * the healthy band. Data is the Nivo shape from
 * `toHeatmapRows(heatmapApi)`.
 */

// Interpolate between two hex colours using a 0..1 `t` ratio.
function lerpHex(a, b, t) {
  const pa = parseInt(a.slice(1), 16);
  const pb = parseInt(b.slice(1), 16);
  const ar = (pa >> 16) & 0xff, ag = (pa >> 8) & 0xff, ab = pa & 0xff;
  const br = (pb >> 16) & 0xff, bg = (pb >> 8) & 0xff, bb = pb & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return `#${((1 << 24) + (r << 16) + (g << 8) + bl).toString(16).slice(1)}`;
}

// emerald-200 → emerald-700 as the healthy green band.
const GREEN_LIGHT = "#a7f3d0";
const GREEN_DARK  = "#047857";
const ORANGE_UNDER = "#f97316";  // < 10%
const RED_OVER     = "#dc2626";  // > 100%
const EMPTY_CELL   = "#14213d";  // 0 utilisation

function heatmapColor(cell) {
  const v = Number(cell?.value);
  if (!Number.isFinite(v)) return EMPTY_CELL;
  if (v <= 0)    return EMPTY_CELL;
  if (v < 10)    return ORANGE_UNDER;
  if (v > 100)   return RED_OVER;
  // Healthy band: 10..100 mapped to GREEN_LIGHT..GREEN_DARK.
  const t = (v - 10) / 90;
  return lerpHex(GREEN_LIGHT, GREEN_DARK, Math.min(1, Math.max(0, t)));
}

export default function UtilisationHeatmap({ data, canSee }) {
  if (!canSee) return null;
  if (!data || data.length === 0) {
    return (
      <ChartCard title="Utilisation Heatmap" subtitle="Per-consultant monthly utilisation (billable-only)">
        <div style={{ textAlign: "center", padding: "56px 0", color: "#6889b4", fontSize: 12 }}>
          No tracker data for this year yet.
        </div>
      </ChartCard>
    );
  }
  const rowCount = data.length;
  const height = Math.max(260, Math.min(720, rowCount * 20));
  return (
    <ChartCard
      title="Utilisation Heatmap"
      subtitle={`Per-consultant monthly utilisation · ${rowCount} billable consultants · ORANGE <10% · GREEN 10–100% · RED >100%`}
      minHeight={height + 80}
    >
      <div style={{ height }}>
        <ResponsiveHeatMap
          data={data}
          margin={{ top: 32, right: 40, bottom: 12, left: 160 }}
          valueFormat=">-.0f"
          colors={heatmapColor}
          axisTop={{ tickSize: 0, tickPadding: 6 }}
          axisLeft={{ tickSize: 0, tickPadding: 6 }}
          axisRight={null}
          axisBottom={null}
          labelTextColor={{ from: "color", modifiers: [["darker", 2]] }}
          theme={NIVO_DARK_THEME}
          animate={false}
          emptyColor={EMPTY_CELL}
        />
      </div>
      <div style={{ display: "flex", gap: 16, marginTop: 10, fontSize: 11, color: "#8fa4c8", flexWrap: "wrap" }}>
        <Swatch color={ORANGE_UNDER} label="< 10% — under-used" />
        <Swatch color={GREEN_LIGHT} label="10% — healthy (lighter = less)" />
        <Swatch color={GREEN_DARK} label="100% — healthy (darker = more)" />
        <Swatch color={RED_OVER} label="> 100% — over-utilised" />
      </div>
    </ChartCard>
  );
}

function Swatch({ color, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ width: 12, height: 12, borderRadius: 3, background: color }} />
      {label}
    </div>
  );
}
